import { atomWithStorage } from "jotai/utils"
import { EventInput } from "@fullcalendar/core";
import { atom, getDefaultStore } from "jotai";
import { Staff } from "./ApiTypes";
import { useAtom } from "jotai";

export enum SearchActivity {
    Hourly  = "hourly",
    Monthly = "monthly"
}

export interface MonthlyForm {
    location:  string;
    startDate: string;
}

export interface SpotSyncAuth {
    isLoggedIn:  boolean;
    account?:    Staff;
    accessToken?:      string;
    roles?:      string[];
    claims?:     Record<string, any>; // Future claims support
}
export interface SpotSyncAuthToken {
    accessToken?:      string;
}

export interface PageMaskOpts {
    showMask: boolean;
    header:   React.ReactNode;
    subText?: string;
}

export interface MaskState {
    showMask: boolean;
    header: string;
    subText: string;
}

// Store the raw auth data
const rawAuthAtom = atomWithStorage<SpotSyncAuth>( "auth", { isLoggedIn: false }, undefined, { getOnInit: true } );
export const authTokenAtom = atomWithStorage<SpotSyncAuthToken>( "authToken", { accessToken: undefined }, undefined, { getOnInit: true } );

// Create a derived authAtom that ensures isLoggedIn is only true when accessToken has a value
export const authAtom = atom(
    (get) => {
        const rawAuth = get(rawAuthAtom);
        const authToken = get(authTokenAtom);
        
        // isLoggedIn should only be true if accessToken has a value
        const isLoggedIn = !!(authToken.accessToken || rawAuth.accessToken);
        
        return {
            ...rawAuth,
            isLoggedIn,
            accessToken: authToken.accessToken || rawAuth.accessToken
        };
    },
    (get, set, newValue: SpotSyncAuth) => {
        // When setting auth, update both atoms
        set(rawAuthAtom, newValue);
        set(authTokenAtom, { accessToken: newValue.accessToken });
    }
);

export const scheduleAtom = atomWithStorage<EventInput[]>( "schedule", [], undefined, { getOnInit: true } );
export const collapseAtom = atom<boolean>( false );
export const redirectAtom = atomWithStorage<string | undefined>( 'redirectPath', undefined, undefined, { getOnInit: true } );
export const maskAtom     = atom<MaskState | undefined>(undefined);
// Add this to your existing atoms
export const toastAtom = atom({
    show: false,
    message: '',
    type: 'success' // or 'danger', 'warning', etc.
  });

// Global loading counter used to manage concurrent API calls
export const loadingCounterAtom = atom<number>(0);

// Removed useHideMaskOnPageChange: mask is managed globally by interceptors

export function showLoadingMask(setMask: (mask: MaskState | undefined) => void) {
    setMask({
        showMask: true,
        header: "Loading...",
        subText: "Please wait while we load the page"
    });
}

// Begin a global loading mask (increments counter)
export function beginGlobalLoadingMask(header: string = "Loading...", subText: string = "Please wait while we load the page") {
    const store = getDefaultStore();
    const current = store.get(loadingCounterAtom);
    const next = current + 1;
    store.set(loadingCounterAtom, next);
    if (current === 0) {
        store.set(maskAtom, {
            showMask: true,
            header,
            subText,
        });
    }
}

// End a global loading mask (decrements counter)
export function endGlobalLoadingMask(forceClear: boolean = false) {
    const store = getDefaultStore();
    const current = store.get(loadingCounterAtom);
    const next = forceClear ? 0 : Math.max(0, current - 1);
    store.set(loadingCounterAtom, next);
    if (next === 0) {
        store.set(maskAtom, undefined);
    }
}